import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CONNECTION_INTENTS, type ConnectionIntent } from '@astrally/shared';
import { useApp } from '../../state/AppState';
import { useI18n } from '../../i18n';
import { ScreenHeader } from '../../components/common';

const INTENT_LABELS: Record<ConnectionIntent, string> = {
  romance: 'Romance',
  friendship: 'Friendship',
  professional: 'Professional',
  founder: 'Founder / business',
  mentorship: 'Mentorship',
  study: 'Study',
  creative: 'Creative collaboration',
  'local-activities': 'Local activities',
  'astrology-discussion': 'Astrology & spiritual discussion',
};

/**
 * The one place the social layer can be switched on.
 *
 * Astrally is private by default: until this screen is completed there is no
 * social component at all. The copy states plainly what changes, because an
 * opt-in that isn't understood isn't consent.
 */
export function SocialOptInScreen() {
  const { t } = useI18n();
  const app = useApp();
  const navigate = useNavigate();
  const [intents, setIntents] = useState<ConnectionIntent[]>(app.user?.intents ?? []);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const intentsRef = useRef<HTMLDivElement>(null);
  const consentRef = useRef<HTMLDivElement>(null);

  const needsIntents = intents.length === 0;
  const needsConsent = !agreed;
  const incomplete = needsIntents || needsConsent;

  /**
   * The CTA stays pressable even when requirements are unmet.
   *
   * It used to be `disabled`, which meant tapping it did nothing at all — no
   * click event, no message, no clue. Two people in a row read that as "the
   * app is broken" rather than "I missed a step". A primary action must always
   * answer a tap, so an incomplete form now says exactly what is left and
   * scrolls to it.
   */
  const enable = async () => {
    if (incomplete) {
      setError(
        needsIntents && needsConsent
          ? t('social.optin.needBoth')
          : needsIntents
            ? t('social.optin.needIntents')
            : t('social.optin.needConsent'),
      );
      const target = needsIntents ? intentsRef.current : consentRef.current;
      // Guarded: jsdom has no scrollIntoView, and it is a nicety rather than
      // part of the contract — the message is what actually unblocks someone.
      target?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      return;
    }
    setBusy(true);
    setError('');
    try {
      await app.setSocialEnabled(true, intents);
      navigate('/discover');
    } catch (e) {
      const raw = String(e);
      setError(
        raw.includes('base-required')
          ? 'Unlock Astrally first — connections are part of the full app.'
          : raw.replace(/^(FirebaseError|Error):\s*/g, '').slice(0, 220),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="screen">
      <ScreenHeader title={t('social.optin.title')} back />

      <div className="banner banner--info small" style={{ marginBottom: 14, lineHeight: 1.55 }}>
        {t('social.optin.currently')}
      </div>

      <div className="card col" style={{ gap: 10, marginBottom: 14 }}>
        <div className="h3">{t('social.optin.whatChanges')}</div>
        {[
          'Your profile becomes visible to a small number of compatible people',
          'Astrally computes compatibility between you and them',
          'You can send connection requests, and receive them',
          'A private chat opens only when both of you agree',
        ].map((line) => (
          <div key={line} className="small">✓ {line}</div>
        ))}
        <div className="divider" style={{ margin: '4px 0' }} />
        <div className="h3">{t('social.optin.whatNever')}</div>
        {[
          'Your exact birth date, time and place are never shared',
          'Your chats with Astrally AI and your memories stay private',
          'There is no public feed, no follower counts, no browsing by strangers',
          'You can switch this off again at any time',
        ].map((line) => (
          <div key={line} className="small muted">• {line}</div>
        ))}
      </div>

      <div ref={intentsRef}>
        <div className="section-title spread">
          <span>{t('social.optin.intents')}</span>
          <span className={intents.length ? 'tiny accent' : 'tiny gold'}>
            {intents.length ? t('social.optin.selected', { n: intents.length }) : t('social.optin.required')}
          </span>
        </div>
        <div className="wrap" style={{ marginBottom: 6 }}>
          {CONNECTION_INTENTS.map((intent) => {
            const on = intents.includes(intent);
            return (
              <button
                key={intent}
                className={`chip ${on ? 'chip--on' : ''}`}
                aria-pressed={on}
                onClick={() => {
                  setError('');
                  setIntents((prev) =>
                    prev.includes(intent) ? prev.filter((i) => i !== intent) : [...prev, intent],
                  );
                }}
              >
                {on && <span aria-hidden>✓</span>}
                {INTENT_LABELS[intent]}
              </button>
            );
          })}
        </div>
        <div className="tiny faint" style={{ marginBottom: 14 }}>
          {t('social.optin.intentsHelp')}
        </div>
      </div>

      <div className="card" ref={consentRef}>
        <label className="row" style={{ cursor: 'pointer', alignItems: 'flex-start' }}>
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => {
              setError('');
              setAgreed(e.target.checked);
            }}
            style={{ marginTop: 4 }}
          />
          <span className="small" style={{ lineHeight: 1.5 }}>{t('onb.consent.body')}</span>
        </label>
      </div>

      <div className="banner banner--gold small" style={{ marginTop: 12 }}>
        {t('safety.disclaimer')}
      </div>

      {error && <div className="banner banner--danger small" style={{ marginTop: 10 }}>{error}</div>}

      <button
        className={`btn btn--primary btn--block ${incomplete ? 'btn--incomplete' : ''}`}
        style={{ marginTop: 16 }}
        disabled={busy}
        onClick={enable}
      >
        {busy ? <span className="spinner" /> : t('social.optin.cta')}
      </button>
      <button className="btn btn--ghost btn--block" style={{ marginTop: 8 }} onClick={() => navigate(-1)}>
        {t('social.optin.stayPrivate')}
      </button>
    </div>
  );
}
